/**
 * Потоковый плеер: один <audio>, грузит ТОЛЬКО текущий трек (preload=metadata),
 * не предзагружает все ~70MB. Источник истины по времени воспроизведения.
 */
export interface AudioSignals {
  bass: number;
  mid: number;
  treble: number;
  rms: number;
  beat: number; // 0..1, затухающий импульс на удар
  level: number; // 0..1, непрерывная энергия (для постоянного мерцания)
  bands: number[]; // спектр по лог-полосам (как эквалайзер)
}
const NBANDS = 24;
const ZERO: AudioSignals = { bass: 0, mid: 0, treble: 0, rms: 0, beat: 0, level: 0, bands: new Array(NBANDS).fill(0) };

export class AudioEngine {
  readonly el: HTMLAudioElement;
  private url: string | null = null;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private freq: Uint8Array<ArrayBuffer> | null = null;
  private bassAvg = 0;
  private beatHold = 0;
  private level = 0;
  private bands: number[] = new Array(NBANDS).fill(0);
  // настройки чувствительности к биту (регулируются дев-панелью)
  readonly tuning = { beatMult: 1.08, beatMin: 0.08, beatDecay: 0.86, levelGain: 1.7 };

  constructor() {
    this.el = new Audio();
    this.el.preload = 'metadata';
  }

  /** Дев-панель: 0..1 — выше = чувствительнее (ниже порог и множитель). */
  setBeatSensitivity(v: number): void {
    const x = Math.max(0, Math.min(1, v));
    this.tuning.beatMult = 1.2 - x * 0.18; // 1.20 (туго) … 1.02 (чутко)
    this.tuning.beatMin = 0.14 - x * 0.1; // 0.14 … 0.04
  }

  /**
   * Подключить Web Audio AnalyserNode к аудио-элементу (один раз, после жеста).
   * MediaElementSource → analyser → destination (звук идёт через граф). Аудио
   * same-origin, поэтому анализатор не tainted. «Ресурсы браузера» — нативный FFT.
   */
  connectAnalyser(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      const Ctx: typeof AudioContext =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaElementSource(this.el);
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      an.smoothingTimeConstant = 0.8;
      src.connect(an);
      an.connect(ctx.destination);
      this.ctx = ctx;
      this.analyser = an;
      this.freq = new Uint8Array(an.frequencyBinCount);
      void ctx.resume();
    } catch {
      /* MediaElementSource уже создан / ctx недоступен */
    }
  }

  /** Управляющие сигналы из FFT текущего трека (bass/mid/treble/rms/beat). */
  signals(): AudioSignals {
    if (!this.analyser || !this.freq) return ZERO;
    this.analyser.getByteFrequencyData(this.freq);
    const avg = (a: number, b: number): number => {
      let s = 0;
      for (let i = a; i < b; i++) s += this.freq![i];
      return s / (b - a) / 255;
    };
    const bass = avg(1, 8);
    const mid = avg(9, 64);
    const treble = avg(65, 200);
    let sum = 0;
    for (let i = 0; i < this.freq.length; i++) {
      const v = this.freq[i] / 255;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.freq.length);
    // чувствительный бит: ниже порог + быстрее адаптация скользящего среднего
    if (bass > this.bassAvg * this.tuning.beatMult && bass > this.tuning.beatMin) this.beatHold = 1;
    this.bassAvg = this.bassAvg * 0.85 + bass * 0.15;
    this.beatHold *= this.tuning.beatDecay;
    // непрерывная энергия (амплифицированная) — чтобы всё мерцало постоянно
    const raw = Math.max(bass, rms * 1.5, (bass + mid + treble) / 3);
    this.level = this.level * 0.55 + raw * 0.45;
    const level = Math.min(1, this.level * this.tuning.levelGain);
    // спектр по лог-полосам (эквалайзер): бины 1..256 → NBANDS полос
    const lo = 1;
    const hi = 256;
    for (let b = 0; b < NBANDS; b++) {
      const f0 = Math.floor(lo * Math.pow(hi / lo, b / NBANDS));
      const f1 = Math.max(f0 + 1, Math.floor(lo * Math.pow(hi / lo, (b + 1) / NBANDS)));
      let s = 0;
      let n = 0;
      for (let i = f0; i < f1 && i < this.freq.length; i++) {
        s += this.freq[i];
        n++;
      }
      this.bands[b] = n ? Math.min(1, (s / n / 255) * 1.6) : 0;
    }
    return { bass, mid, treble, rms, beat: this.beatHold, level, bands: this.bands };
  }

  load(url: string): void {
    if (this.url === url) return;
    this.url = url;
    this.el.src = url;
    this.el.load();
  }

  async play(): Promise<void> {
    try {
      await this.el.play();
    } catch {
      /* autoplay заблокирован до жеста — норма */
    }
  }

  /**
   * Немедленный старт эфира: пробуем со звуком (вернувшимся/PWA-юзерам браузер
   * разрешает), при блоке — БЕЗ звука (muted autoplay почти всегда разрешён),
   * чтобы лента/визуал шли сразу. Полный звук включит unmute() по первому жесту.
   */
  async autostart(): Promise<void> {
    if (!this.el.paused) return; // уже идёт
    try {
      await this.el.play();
    } catch {
      try {
        this.el.muted = true;
        await this.el.play();
      } catch {
        /* даже muted нельзя — ждём жеста */
      }
    }
  }

  /** Включить звук (после первого жеста пользователя). */
  unmute(): void {
    this.el.muted = false;
  }

  pause(): void {
    this.el.pause();
  }

  get time(): number {
    return this.el.currentTime || 0;
  }
  get duration(): number {
    return Number.isFinite(this.el.duration) ? this.el.duration : 0;
  }
  get paused(): boolean {
    return this.el.paused;
  }

  onEnded(cb: () => void): void {
    this.el.addEventListener('ended', cb);
  }
  onTime(cb: () => void): void {
    this.el.addEventListener('timeupdate', cb);
  }
}
